import * as Sentry from '@sentry/nextjs'
import uniqBy from 'lodash/uniqBy'
import Router from 'next/router'
import { type AnyAction } from 'redux'
import { ofType } from 'redux-observable'
import { from } from 'rxjs'
import { catchError, mergeMap, switchMap } from 'rxjs/operators'

import { FbEventName, sendFbEventByName } from '@/legacy-src/analytics/fbPixelData'
import ValidationRules from '@/legacy-src/utilities/validationRules'

import { getHasCompleteAddress } from '@/src/utils/getHasCompleteAddress'

import { addGtmEvent, addGtmUserInAppEvent } from '../analytics/gtm'
import useFlags from '../hooks/useFlags'
import ClientActions from '../redux/ClientRedux'
import DiagnosisActions from '../redux/DiagnosisRedux'
import GeneralActions from '../redux/GeneralRedux'
import TherapistActions from '../redux/TherapistRedux'
import UserActions, { USER_TYPE, UserTypes } from '../redux/UserRedux'
import { type ApiEndpoints } from '../services/api'
import type { ReduxState } from '../store/reducers'
import { convertArrayToObject, convertArrayToObjectNoDuplicateKey } from '../utilities/arrayTobject'
import { setTokens } from '../utilities/setTokens'
import Toaster from '../utilities/toaster'
import { generalErrorHandler, generalSuccessHandler } from './EpicHelpers'

type Api = { [key: string]: any }

const processSuccessfulLogin = (
  data: any,
  apis: any,
  therapistId: any,
  isDiagnosis: boolean,
  diagnosisData?: any,
  redirectUrl?: string,
  sendEvent = true,
) => {
  setTokens({
    api: apis.api,
    data: {
      refreshToken: data.refreshToken,
      accessToken: data.accessToken,
    },
  })
  return from([
    GeneralActions.onChangeGeneralValue('emailIsInOtherCountry', !!data.emailInOtherCountry),
    UserActions.getUserDataRequest(
      therapistId,
      isDiagnosis,
      sendEvent
        ? {
            event: 'loginSuccessful',
          }
        : {},
      diagnosisData,
      redirectUrl,
    ),
  ])
}

export default class UserEpic {
  static getAllEvents = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(UserTypes.GET_ALL_EVENTS_REQUEST),
      mergeMap(({ userType }) =>
        from(userType === USER_TYPE.THERAPIST ? api.getCurrentEvents() : api.getAllEvents()).pipe(
          mergeMap((response: any) => {
            const filteredData = uniqBy(response.data, 'id')
            const actions: AnyAction[] = []
            if (userType === USER_TYPE.THERAPIST) {
              actions.push(TherapistActions.onChangeTherapistValue('appointments', filteredData))
            } else {
              actions.push(
                ClientActions.onChangeClientValue('appointments', convertArrayToObject(filteredData, 'therapistId')),
              )
            }
            actions.push(generalSuccessHandler('', 'allAppointments'))
            return from(actions)
          }),
          catchError(generalErrorHandler('allAppointments')),
        ),
      ),
    )

  static updateUserData = (api: ApiEndpoints) => (action$: any, state) => {
    const { showPlaceCategoryInAddress } = useFlags()

    return action$.pipe(
      ofType(UserTypes.UPDATE_USER_DATA_REQUEST),
      switchMap(({ data }) =>
        from(api.updateUserData(data)).pipe(
          mergeMap(() => {
            return from([
              generalSuccessHandler('component.toaster.userDataUpdateSuccess', 'updateUserData'),
              UserActions.onMergeUserValue({
                ...data,
                hasCompleteAddress: getHasCompleteAddress({ ...state.value.user, ...data }, showPlaceCategoryInAddress),
              }),
              //
            ])
          }),
          catchError(generalErrorHandler('updateUserData')),
        ),
      ),
    )
  }

  static login = (apis: Api) => (action$: any) =>
    action$.pipe(
      ofType(UserTypes.LOGIN_REQUEST),
      switchMap(({ email, password, therapistId, diagnosis, redirectUrl, companyEmployee }) =>
        from(apis.next.login(email, password, companyEmployee)).pipe(
          mergeMap((response: any) => {
            Sentry.setUser({ email })
            return processSuccessfulLogin(response.data, apis, therapistId, diagnosis, undefined, redirectUrl)
          }),
          catchError((error) =>
            generalErrorHandler('authorization', [
              GeneralActions.onChangeGeneralValue(
                'emailIsInOtherCountry',
                !!error.response?.data?.emailInOtherCountry || false,
              ),
            ])(error),
          ),
        ),
      ),
    )
  static loginFacebook = (apis: Api) => (action$: any, state: any) =>
    action$.pipe(
      ofType(UserTypes.LOGIN_REQUEST_FACEBOOK),
      switchMap(({ code, therapistId, diagnosis, redirectUrl, companyEmployee, doNotCreateNewUser }) =>
        from(apis.next.loginFacebook(code, companyEmployee, doNotCreateNewUser)).pipe(
          mergeMap((response: any) => {
            Sentry.setUser({ email: state.value.user.email })
            const isRegistration = response.status === 201
            if (isRegistration) {
              addGtmEvent('registrationFinished', diagnosis ? 'diagnoseView' : 'dashboardView', {
                email: state.value.user.email,
              })
              sendFbEventByName({
                eventName: FbEventName.CompleteRegistration,
                user: { email: state.value.user.email },
              })
              Toaster.success('component.toaster.registrationSuccess')
            }
            return processSuccessfulLogin(
              response.data,
              apis,
              therapistId,
              !!diagnosis,
              diagnosis,
              redirectUrl,
              !isRegistration,
            )
          }),
          catchError((error) => {
            const response = error.response?.data
            if (response.type === 'NOT_FOUND') {
              Router.push({
                pathname: '/login',
                query: { ...Router.query, socialUser: response.socialUser, kind: 'fb' },
              })
            } else if (response.type === 'NO_SUCH_VERIFICATION') {
              Router.push('/login')
            } else {
              Toaster.error('errors.defaultError')
              Router.push('/login')
            }
            return generalErrorHandler('authorization')(error)
          }),
        ),
      ),
    )
  static loginGoogle = (apis: Api) => (action$: any, state: any) =>
    action$.pipe(
      ofType(UserTypes.LOGIN_REQUEST_GOOGLE),
      switchMap(({ idToken, therapistId, diagnosis, redirectUrl, doNotCreateNewUser, companyEmployee }) => {
        return from(apis.next.loginGoogle(idToken, companyEmployee, doNotCreateNewUser)).pipe(
          mergeMap((response: any) => {
            Sentry.setUser({ email: state.value.user.email })
            const isRegistration = response.status === 201
            if (isRegistration) {
              addGtmEvent('registrationFinished', diagnosis ? 'diagnoseView' : 'dashboardView', {
                email: state.value.user.email,
              })
              sendFbEventByName({
                eventName: FbEventName.CompleteRegistration,
                user: { email: state.value.user.email },
              })
              Toaster.success('component.toaster.registrationSuccess')
            }
            return processSuccessfulLogin(
              response.data,
              apis,
              therapistId,
              diagnosis,
              undefined,
              redirectUrl,
              !isRegistration,
            )
          }),
          catchError((error) => {
            const response = error.response?.data
            if (response.type === 'NOT_FOUND') {
              Router.push({ query: { ...Router.query, socialUser: response.socialUser, kind: 'google' } })
            } else if (response.type === 'NO_SUCH_VERIFICATION') {
              Router.push('/login')
            } else {
              Toaster.error('errors.defaultError')
              Router.push('/login')
            }
            return generalErrorHandler('authorization')(error)
          }),
        )
      }),
    )
  static getUserData = (api: ApiEndpoints) => (action$: any, _state$: { value: ReduxState }) => {
    const { showPlaceCategoryInAddress } = useFlags()

    return action$.pipe(
      ofType(UserTypes.GET_USER_DATA_REQUEST),
      switchMap(({ therapistId, withoutRedirect, currentPathname, gtm, diagnosisData, redirectUrl }) =>
        from(api.getUserData()).pipe(
          mergeMap((response: any) => {
            const user = {
              ...response.data,
              hasCompleteAddress: getHasCompleteAddress(response.data, showPlaceCategoryInAddress),
            }

            Sentry.setUser({ email: user.email })

            let url
            if (user.type === USER_TYPE.CLIENT) {
              if (typeof therapistId === 'number' && !Number.isNaN(therapistId)) {
                // this takes care of the redirect to checkout page in single-booking
                const isSlotSelected = Boolean(Router.query.startDate && Router.query.endDate && Router.query.service)
                if (isSlotSelected) gtm.destination = 'profileView'

                url = `/client/${therapistId}/profile`
              } else {
                url = '/client/calendar'
              }
            } else {
              url = '/therapist/appointments'
            }

            // Always redirect logged user from the login page
            if (!withoutRedirect || currentPathname?.includes('/login')) {
              const isSafeRedirectUrl = redirectUrl && ValidationRules.isSafeRedirectUrl(redirectUrl)
              if (redirectUrl && !isSafeRedirectUrl) {
                Sentry.captureMessage(`Attempt at unsafe redirect URL: ${redirectUrl}`)
              }

              if (isSafeRedirectUrl) {
                Router.push(redirectUrl)
              } else {
                Router.push({ pathname: url, query: Router.query })
              }
            }

            if (gtm?.event) {
              addGtmUserInAppEvent(
                {
                  ...gtm,
                  email: user.email,
                  phone: user.phone,
                  id: user.id,
                },
                user.type === USER_TYPE.THERAPIST ? 'therapist' : 'client',
              )
            }

            const actions = [
              GeneralActions.onStopFetching(['authorization']),
              UserActions.onMergeUserValue(user),
              DiagnosisActions.getDiagnosisRequest(diagnosisData),
            ]
            if (user.type === USER_TYPE.THERAPIST) {
              actions.push(
                TherapistActions.getScheduleRequest(),
                TherapistActions.getAllClientsRequest(),
                UserActions.getAllEventsRequest(user.type),
                TherapistActions.getTherapistDataRequest(user.id),
              )
            } else {
              actions.push(ClientActions.getTherapistsRequest(), UserActions.getAllEventsRequest(user.type, user.id))
            }
            return from(actions)
          }),
          catchError(generalErrorHandler('authorization')),
        ),
      ),
    )
  }

  static getTherapistSpecializations = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(UserTypes.GET_THERAPIST_SPECIALIZATIONS_REQUEST),
      switchMap(() =>
        from(api.getSpecializations()).pipe(
          mergeMap((response: any) => {
            const convertedData = convertArrayToObjectNoDuplicateKey(response.data, 'id', 'name')
            return from([
              TherapistActions.onChangeTherapistValue('therapistSpecializations', convertedData),
              ClientActions.onChangeClientValue('therapistSpecializations', convertedData),
              generalSuccessHandler('', 'general'),
            ])
          }),
          catchError(generalErrorHandler()),
        ),
      ),
    )

  static getAllActions = (apis: Api) => {
    const { api } = apis
    return [
      UserEpic.getAllEvents(api),
      UserEpic.updateUserData(api),
      UserEpic.login(apis),
      UserEpic.loginFacebook(apis),
      UserEpic.loginGoogle(apis),
      UserEpic.getUserData(api),
      UserEpic.getTherapistSpecializations(api),
    ]
  }
}
